import { WorksheetLinkingFormData } from './LinkPriorWorksheetsForm';
import React, { Fragment } from 'react';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';
import { ButtonV2 } from '@cimplico/components';
import { CheckCircleIcon, ExclamationCircleIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import plural from '../../utilities/plural';
import { useFirmContext } from '../../providers/FirmProvider';

type LinkingBannerProps = {
    priorWorkpaperId: string;
    accountWorksheets: boolean;
    isLoading?: boolean;
    isSaving?: boolean;
    bannerRef?: React.RefObject<HTMLDivElement>;
};

function LinkingBanner({ priorWorkpaperId, accountWorksheets, isLoading, isSaving, bannerRef }: LinkingBannerProps): JSX.Element {
    // subscribe to formState.dirtyFields to re-render when any worksheetLinkings are dirty
    const {
        control,
        formState: { dirtyFields },
    } = useFormContext<WorksheetLinkingFormData>();

    const worksheetLinkings = useWatch({ control, name: 'worksheetLinkings' }) || [];
    const unlinked = worksheetLinkings?.filter((wsl) => !wsl.priorWorksheet)?.length;
    const changes = worksheetLinkings?.filter((wsl) => wsl.priorWorksheet)?.length;

    const { shortId } = useFirmContext();

    return (
        <div className="rounded-md border px-4 py-2 mb-2 flex items-center">
            <div className="mr-4">
                <div>
                    <div className="flex items-center gap-1">
                        <ArrowsRightLeftIcon className="h-9 w-9 border rounded-md p-1 shrink-0" />
                    </div>
                </div>
            </div>
            <a
                href={`/${shortId}/workpapers/${priorWorkpaperId}/${accountWorksheets ? 'trial-balance' : 'other-schedules'}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <ButtonV2 size="xs" type="secondary" label="Go To Prior Worksheets" />
            </a>
            <div className="flex-1"></div>
            <div className="flex items-center gap-2">
                {isLoading ? (
                    <div className="animate-pulse bg-gray-300 w-[150px] h-[20px] rounded"></div>
                ) : (
                    <Fragment>
                        {unlinked > 0 ? (
                            <p className="text-sm text-orange-400 flex items-center">
                                <ExclamationCircleIcon className="h-5 w-5 mr-0.5" />
                                {unlinked} {plural(unlinked, 'worksheet is', 'worksheets are')} not linked to a prior.
                            </p>
                        ) : (
                            <p className="text-sm text-green-600 flex items-center">
                                <CheckCircleIcon className="h-5 w-5 mr-0.5" /> All worksheets are linked to a prior.
                            </p>
                        )}
                    </Fragment>
                )}
                <span className="w-[1px] h-6 bg-gray-300" />
                <ButtonV2
                    type="secondary"
                    label={`Link ${plural(changes, 'worksheet', 'worksheets')} (${changes})`}
                    size="sm"
                    isDisabled={isLoading || isSaving || changes === 0}
                    isLoading={isSaving}
                    isSubmit
                    onClick={(): void => {
                        // Scroll to the top of the page
                        bannerRef?.current?.scrollIntoView({ block: 'end' });
                    }}
                />
            </div>
        </div>
    );
}

export default LinkingBanner;
